import React, { useEffect, useState } from 'react';
import { Timer, Bell, Check } from 'lucide-react';
import { pushToDataLayer } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;
const pad = (n) => String(n).padStart(2, '0');

export function useCountdown(targetIso) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!targetIso) { setLeft(null); return; }
    const tick = () => {
      const ms = new Date(targetIso).getTime() - Date.now();
      if (ms <= 0) { setLeft(null); return; }
      setLeft({
        d: Math.floor(ms / 86400000),
        h: Math.floor(ms / 3600000) % 24,
        m: Math.floor(ms / 60000) % 60,
        s: Math.floor(ms / 1000) % 60,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return left;
}

// Slim self-fetching countdown line for the Shop page City Series banner.
export const NextDropBannerLine = () => {
  const [drop, setDrop] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/city-series/next-drop`)
      .then((r) => r.json())
      .then((d) => setDrop(d.next_drop))
      .catch(() => {});
  }, []);
  const left = useCountdown(drop?.drops_at);
  if (!drop || !left) return null;
  return (
    <p
      className="inline-flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-[0.2em] mt-3"
      style={{ fontFamily: 'Oswald, sans-serif' }}
      data-testid="next-drop-banner-line"
    >
      <Timer className="w-3.5 h-3.5" />
      Next: {drop.title} · {left.d}d {pad(left.h)}h {pad(left.m)}m {pad(left.s)}s
    </p>
  );
};

// Slim self-fetching countdown line for the Shop page Women's Collection banner.
export const WomensDropBannerLine = () => {
  const [drop, setDrop] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/womens-collection/next-drop`)
      .then((r) => r.json())
      .then((d) => setDrop(d.next_drop))
      .catch(() => {});
  }, []);
  const left = useCountdown(drop?.drops_at);
  if (!drop || !left) return null;
  return (
    <p
      className="inline-flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-[0.2em] mt-3"
      style={{ fontFamily: 'Oswald, sans-serif' }}
      data-testid="womens-drop-banner-line"
    >
      <Timer className="w-3.5 h-3.5" />
      Drops in {left.d}d {pad(left.h)}h {pad(left.m)}m {pad(left.s)}s
    </p>
  );
};

// Hero countdown block + first-access waitlist form for the City Series page.
export const NextDropHero = ({ drop }) => {
  const left = useCountdown(drop?.drops_at);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error
  if (!drop || !left) return null;

  const join = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState('sending');
    try {
      const res = await fetch(`${API}/api/leads/dojo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magnet_id: 'city_series_waitlist',
          source: 'city-series-next-drop',
          metadata: { drop: drop.title },
        }),
      });
      if (!res.ok) throw new Error('failed');
      setState('done');
      pushToDataLayer('waitlist_join', {
        waitlist: 'city_series_next_drop',
        drop: drop.title,
        source: 'city-series-next-drop',
      });
    } catch {
      setState('error');
    }
  };

  const units = [
    [left.d, left.d === 1 ? 'Day' : 'Days'],
    [pad(left.h), 'Hrs'],
    [pad(left.m), 'Min'],
    [pad(left.s), 'Sec'],
  ];

  return (
    <div className="mt-10 border border-[#C7FA3E]/30 bg-[#0d1004] p-6 md:p-8" data-testid="next-drop-hero">
      <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
        Up next · {drop.title}
      </p>
      <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
        <div className="flex gap-4 md:gap-6" data-testid="next-drop-countdown">
          {units.map(([val, label]) => (
            <div key={label} className="text-center">
              <div className="text-white text-4xl md:text-5xl leading-none" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{val}</div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mt-1.5">{label}</div>
            </div>
          ))}
        </div>
        <div className="flex-1 md:max-w-md">
          {state === 'done' ? (
            <p className="inline-flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-widest" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="next-drop-waitlist-success">
              <Check className="w-4 h-4" /> You&apos;re on the list — first access secured.
            </p>
          ) : (
            <>
              <form onSubmit={join} className="flex gap-2" data-testid="next-drop-waitlist-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={state === 'sending'}
                  className="flex-1 min-w-0 px-4 py-2.5 bg-black/50 border border-zinc-700 text-white text-sm focus:outline-none focus:border-[#C7FA3E] transition-colors"
                  data-testid="next-drop-waitlist-email"
                />
                <button
                  type="submit"
                  disabled={state === 'sending'}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#C7FA3E] hover:bg-[#b8e01f] disabled:opacity-60 text-black text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
                  data-testid="next-drop-waitlist-submit"
                >
                  <Bell className="w-3.5 h-3.5" /> {state === 'sending' ? 'Joining…' : 'Get first access'}
                </button>
              </form>
              {state === 'error' && (
                <p className="text-red-400 text-xs mt-2" data-testid="next-drop-waitlist-error">Something went wrong — try again.</p>
              )}
              <p className="text-zinc-500 text-xs mt-2">One email when it drops. First access before anyone else.</p>
            </>
          )}
        </div>
      </div>
      {(drop.teasers || []).length > 0 && (
        <div className="mt-7 pt-6 border-t border-zinc-800/60" data-testid="next-drop-teasers">
          <p className="text-zinc-500 text-[10px] uppercase tracking-[0.25em] mb-3">Sneak peek — designs unlock on drop day</p>
          <div className="flex flex-wrap gap-3">
            {drop.teasers.map((src) => (
              <div key={src} className="relative w-24 h-24 md:w-28 md:h-28 overflow-hidden border border-zinc-800 bg-black" data-testid="next-drop-teaser-tile">
                <img
                  src={src.startsWith('http') ? src : `${API}${src}`}
                  alt="Next drop sneak peek"
                  loading="lazy"
                  className="w-full h-full object-cover blur-[6px] brightness-[1.9] contrast-110 scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#C7FA3E]/20 via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[#C7FA3E] text-2xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>?</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
